import React, { useState } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  InputLabel
} from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';

/**
 * Password Component
 * 
 * A reusable functional component that renders a password input field with show/hide toggle functionality.
 * Can be used for any password input needs throughout the application.
 * 
 * @param {string} value - The current value of the password input
 * @param {function} onChange - Callback function called when the input value changes
 * @param {object} classes - Material-UI classes object for styling
 * @param {boolean} disabled - Whether the input should be disabled (default: false)
 * @param {string} label - The label text for the password field (default: "Password")
 * @param {string} placeholder - The placeholder text for the input field (default: "Enter password")
 * @param {boolean} showLabel - Whether to show the label above the input (default: true)
 * @param {string} variant - The variant of the TextField (default: "outlined")
 * @param {string} margin - The margin of the TextField (default: "dense")
 * @param {boolean} fullWidth - Whether the input should take full width (default: true)
 * @param {boolean} required - Whether the field is required (default: false)
 * @param {string} helperText - Helper text to display below the input
 * @param {boolean} error - Whether the input is in error state (default: false)
 * @param {string} name - The name attribute for the input field
 * @param {string} id - The id attribute for the input field
 * @param {function} onBlur - Callback function called when the input loses focus
 * @param {function} onFocus - Callback function called when the input gains focus
 * @param {boolean} autoComplete - Whether to enable browser autocomplete (default: false)
 * @param {boolean} autoFocus - Whether to auto-focus the input on mount (default: false)
 */
const Password = ({ 
  value, 
  onChange, 
  classes, 
  disabled = false,
  label = "Password",
  placeholder = "Enter password",
  showLabel = true,
  variant = "outlined",
  margin = "dense",
  fullWidth = true,
  required = false,
  helperText,
  error = false,
  name,
  id,
  onBlur,
  onFocus,
  autoComplete = false,
  autoFocus = false
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box className={classes?.passwordWrapper}>
      {showLabel && (
        <InputLabel className={classes?.inputLabel} required={required}>
          {label}
        </InputLabel>
      )}
      <TextField
        type={showPassword ? "text" : "password"}
        value={value || ''}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        variant={variant}
        margin={margin}
        fullWidth={fullWidth}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        error={error}
        helperText={helperText}
        name={name}
        id={id}
        autoComplete={autoComplete ? "current-password" : "off"}
        autoFocus={autoFocus}
        className={classes?.input}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                tabIndex="-1"
                onClick={handleTogglePasswordVisibility}
                edge="end"
                size="small"
                disabled={disabled}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          )
        }}
      />
    </Box>
  );
};

export default Password;
